import {
  AssessmentColumnType as ColumnType,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { Button } from '@components/ui'
import { useQuery } from '@tanstack/react-query'
import { LevelOptions } from '@utils/Variables'
import { useDropdownMenu } from '@utils/assessmentReportUtils'
import { objToLevSelOpts } from '@utils/enumHelper'
import { Flex } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { Database_Procs_TeamBonusGames_Item } from '@suntex.fim/api-client'
import {
  getAssessmentReportData,
  getValidAssessmentOption,
  teamAssessmentInitialSorting
} from '@suntex.fim/assessment'
import { useFramework } from '@suntex.fim/framework'
import { SendMessage } from '@suntex.fim/messages'
import { useAppQueryString } from '@suntex.fim/nav'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import {
  ExportMenu,
  LevelSelect,
  LoaderOverlay,
  SearchInput
} from '@suntex.fim/ui'

import { appSessionStorage } from '../../utils/storageUtils'

import columns, {
  getCTColumnKeys,
  hidableSelectLabel
} from './AssessmentReport/AssessmentReportCTTable'
import AssessmentReportNavigation from './AssessmentReport/AssessmentReportNavigation'
import AssessmentReportSummaryDashboard from './AssessmentReport/AssessmentReportSummaryDashboard'
import SelectedRowPopUp from './AssessmentReport/SelectedRowPopUp'

const levelOptions = objToLevSelOpts(LevelOptions)

/**
 * returns JSX for computation thinking report
 */
function AssessmentReportCT() {
  const { api } = useFramework()
  const { ctLevel } = useAppQueryString()
  const team = useOptionalTeamInfo()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const selectedLevel = {
    level: getValidAssessmentOption(
      levelOptions,
      Number(ctLevel) || appSessionStorage.ctLevel || levelOptions[0]!.key
    )
  }
  const [filters, setFilters] = useState(selectedLevel)
  const [sendMessageData, setSendMessageData] = useState<any[]>([])
  /**
   * open modal for send message and set sendMessageData state value
   */
  const sendMessage = (data: any[]) => {
    setIsModalOpen(true)
    setSendMessageData(data)
  }
  const memoColumns = useMemo<ColumnType<Database_Procs_TeamBonusGames_Item>[]>(
    () => columns({ level: filters.level, graphColor: '#3FB2FF', sendMessage }),
    [filters.level]
  )

  const { data: assessmentReportData, isLoading } = useQuery({
    queryKey: ['assessment', 'ct', team?.id],
    queryFn: async () => {
      const response = await api.get_Assessment_BonusTeam({
        teamId: team!.id
      })
      return response.teamBonusGames || []
    },
    enabled: team?.id != null
  })
  const assessmentReport = getAssessmentReportData(assessmentReportData)

  /**
   * Initialize usetable with initial data and
   * Destructure table and Table from useTable hooks
   */
  const { table, Table } =
    useAssessmentTable<Database_Procs_TeamBonusGames_Item>({
      data: assessmentReport,
      columns: memoColumns,
      isLoading,
      globalFilter: searchQuery,
      initialSorting: teamAssessmentInitialSorting,
      horizontalScroll: true
    })

  const selectedRow = table.getSelectedRowModel().flatRows
  // Update localStorage whenever level changes
  useEffect(() => {
    appSessionStorage.ctLevel = filters.level
  }, [filters.level])
  const isDisabled = !selectedRow.length

  const sendMessageUsers = sendMessageData.length
    ? sendMessageData
    : selectedRow
  const selectedUserOptions = sendMessageUsers.map(
    ({ original }: { original: Database_Procs_TeamBonusGames_Item }) => ({
      label: original.displayName || original.userId,
      value: original.playerId
    })
  )
  const columnKeys = getCTColumnKeys(filters.level)
  const linkOptions = columnKeys.map((label) => ({
    key: `word-problems/${filters.level}/${label}`,
    label: hidableSelectLabel[label]
  }))

  const items = useDropdownMenu({
    table,
    hidableSelectLabel,
    fileName: `${team?.name}_CTT`,
    setIsLoading: setIsExporting
  })
  return (
    <>
      {/* navigation */}
      <Flex vertical gap="20px" style={{ position: 'relative' }}>
        {/* Loader overlay when loading and popup not open */}
        <LoaderOverlay isLoading={isLoading || isExporting} />
        <AssessmentReportNavigation
          exportButton={
            <ExportMenu
              ariaLabel="Export report options"
              items={items}
              trigger={['click']}
            />
          }
          filters={
            <Flex gap={14}>
              <Flex gap={6} flex="1 1 auto" vertical>
                <p className="dd-label fw-bold text-sm">Level</p>
                <LevelSelect
                  value={filters.level}
                  onChange={(level) => {
                    setFilters((prev) => ({ ...prev, level }))
                    setSearchQuery('')
                  }}
                  options={levelOptions}
                />
              </Flex>
            </Flex>
          }
        />
        <Flex className="tiles-container" wrap="wrap">
          <Flex className="left" vertical gap="20px" flex="1 1 560px">
            <AssessmentReportSummaryDashboard />
          </Flex>
        </Flex>
        {/* result */}
        <div className="main-result">
          {/* Updated toolbar design */}
          <Flex
            justify="space-between"
            align="center"
            className="main-result__toolbar"
            wrap
          >
            <h4 className="title">Team Players</h4>

            <Flex
              gap="10px"
              justify="space-between"
              wrap
              className="print-hidden"
            >
              <div className="report-search">
                <SearchInput value={searchQuery} onChange={setSearchQuery} />
              </div>
              <Button
                intent="tertiary"
                size="small"
                disabled={isDisabled}
                onClick={() => setIsModalOpen(true)}
              >
                SEND MESSAGE
              </Button>
            </Flex>
          </Flex>

          <Table />
        </div>
        {!isModalOpen && (
          <SelectedRowPopUp
            selectedRow={selectedRow}
            resetRowSelection={table.resetRowSelection}
            handleSendMessage={() => setIsModalOpen(true)}
          />
        )}
        <SendMessage
          level={levelOptions[filters.level]?.label}
          linkOptions={linkOptions}
          isModalOpen={isModalOpen}
          closeModal={(isSuccess) => {
            setIsModalOpen(false)
            setSendMessageData([])
            if (isSuccess) {
              table.resetRowSelection()
            }
          }}
          selectedUserOptions={selectedUserOptions}
          recipientEntityId={team?.id}
          reportType="Computational Thinking"
        />
      </Flex>
    </>
  )
}
export default AssessmentReportCT
